/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { createRef, forwardRef, useState, useEffect } from "react";
import { Select, MenuItem, FormControl, TextField, Popover, Checkbox } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import ButtonComponent from "../../form/ButtonComponent";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import axiosWrapper from "../../../services/axiosWrapper";
import get from "lodash/get";
import Text from "../Text/Text";
// Type-only, so the module cycle with ./index (which imports this file's types) is erased at build.
import type { QueryOperator } from "./index";

type FilterType = "Text" | "Date" | "Number" | "MultiSelect";

/**
 * A grid row as this popover reads it. The row shape belongs to whichever grid mounts the filter, so
 * it is only ever indexed (by `field`, or through the column's `_valueGetter`), never destructured.
 */
type FilterRow = Record<string, unknown>;

/** The SlickGrid column-header descriptor whose first button carries the filter icon's css class. */
type FilterColumnHeader = { buttons?: Array<{ cssClass?: string }> };

export type ColumnDefinition = {
  field: string;
  _valueGetter?: (row: FilterRow) => unknown;
  header?: FilterColumnHeader;
  filterApplied?: boolean;
};

/** A column as it comes back from `slickGrid.getColumns()` — the header is always populated there. */
type FilterGridColumn = ColumnDefinition & { header: Required<FilterColumnHeader> };

/** The slice of the grid instance this popover reaches, to repaint the filter icon on a column. */
type FilterGridInstance = {
  slickGrid: { getColumns: () => FilterGridColumn[]; setColumns: (columns: FilterGridColumn[]) => void };
};

/** One leaf predicate emitted into the grid's `where` clause. */
type FilterPredicate = {
  field: string;
  operator: string;
  value?: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
};

/** A composite (`and` / `or`) group. The outer `and` group nests the per-column `or` groups. */
type FilterPredicateGroup = {
  isComplex: true;
  ignoreCase: boolean;
  ignoreAccent: boolean;
  condition: "and" | "or";
  predicates: Array<FilterPredicate | FilterPredicateGroup>;
};

/** The whole `where` clause: the composite group, `""` when nothing is filtered, or `[]` before build. */
type FilterWhereClause = FilterPredicateGroup | "" | never[];

/** What the text / date / multi-select inputs hold, keyed off the column's filter type. */
type FilterText = string | number | string[] | { date?: Dayjs } | { startDate?: Dayjs; endDate?: Dayjs } | undefined;

/**
 * One column's stored filter state. Kept structurally interchangeable with the grid's own
 * `FilterCondition` (SlickGrid/index.tsx) — same four required members — so the `filterConditions`
 * state and its setter pass across the boundary in both directions. The extra member is optional and
 * therefore does not break that: the grid's copy omits `predicate`, which only this component writes.
 */
export type FilterCondition = {
  id: string;
  type: FilterType;
  option: QueryOperator;
  text: unknown;
  predicate?: FilterPredicate[];
};

type AnchorPosition = { top: number; left: number } | null;
type FilterConditionsMap = Record<string, FilterCondition>;
type FilterOptionKey =
  | "startswith"
  | "endswith"
  | "empty"
  | "equal"
  | "contains"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "notequal"
  | "notempty"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "between"
  | "null"
  | "notnull";
type DefaultFilterText = string | number | { date?: Dayjs } | { startDate?: Dayjs; endDate?: Dayjs } | string[];
type DefaultFilterOption = { option: FilterOptionKey; text: DefaultFilterText };
export type Props = {
  currentColumnId: string;
  setCurrentColumnId: (id: string) => void;
  setFilterPredicates: React.Dispatch<React.SetStateAction<string | Record<string, unknown>>>;
  anchorEl: AnchorPosition;
  setAnchorEl: (pos: AnchorPosition) => void;
  filterType?: FilterType;
  filterOpened: boolean;
  setFilterOpened: (open: boolean) => void;
  remoteDataSource?: string;
  /** The grid's own response hook; it is handed the raw axios response and returns the row array. */
  beforeDataBound?: (data: unknown) => unknown;
  columnDefinitions: ColumnDefinition[];
  setAppliedFilters: React.Dispatch<React.SetStateAction<string[]>>;
  datasetForFetchingOptions: FilterRow[];
  query?: { where?: unknown[]; [key: string]: unknown };
  localData?: boolean;
  appliedFilters: string[];
  filterConditions: FilterConditionsMap;
  setFilterConditions: React.Dispatch<React.SetStateAction<FilterConditionsMap>>;
  getUpdatedFilterConditions: (conditions: FilterConditionsMap) => FilterConditionsMap;
  applyFilter: (
    data: FilterRow[],
    field: string,
    filterOption: string,
    text: FilterText,
    filterType: FilterType,
  ) => FilterRow[];
  preAppliedFilters?: Record<string, FilterCondition>;
  onFilterConditionsChange?: (activeKeys: string[], conditions: FilterConditionsMap) => void;
  /**
   * KNOWN MISMATCH (left as-is — fixing it changes runtime): the grid declares this prop as
   * `(option: unknown) => ReactNode`, but this component invokes it as `(props, option, columnId)`.
   * A consumer that supplies it therefore receives the `<li>` props where it expects the option
   * value. Typed here by what is actually passed.
   */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: string, columnId: string) => React.ReactNode;
};

const FILTER_MAPPER: Record<FilterType, string[]> = {
  Text: [
    "Starts With",
    "Ends With",
    "Empty",
    "Equal",
    "Contains",
    "Does Not Start With",
    "Does Not End With",
    "Does Not Contain",
    "Not Equal",
    "Not Empty",
  ],
  Date: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Between",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  Number: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  MultiSelect: ["Equal"],
};

const TEXT_FIELD_DISABLED_OPTIONS = new Set<FilterOptionKey>(["empty", "null", "notnull", "notempty"]);

const DEFAULT_FILTER_OPTIONS: Record<FilterType, DefaultFilterOption> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
  MultiSelect: { option: "equal", text: [] },
};

const FilterComponent = forwardRef<unknown, Props>(
  (
    {
      currentColumnId,
      setCurrentColumnId,
      setFilterPredicates,
      anchorEl,
      setAnchorEl,
      filterType,
      filterOpened,
      setFilterOpened,
      remoteDataSource,
      beforeDataBound,
      columnDefinitions,
      setAppliedFilters,
      datasetForFetchingOptions,
      query,
      localData,
      appliedFilters,
      filterConditions,
      setFilterConditions,
      getUpdatedFilterConditions,
      applyFilter,
      preAppliedFilters,
      onFilterConditionsChange,
      renderOption,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // Callers always pass an object ref (`useRef`), never a callback ref.
    const gridRef = (ref as React.MutableRefObject<FilterGridInstance>) || createRef<FilterGridInstance>();
    const [tempFilterOption, setTempFilterOption] = useState<FilterOptionKey | "">("");
    const [tempFilterText, setTempFilterText] = useState<FilterText>("");
    const [options, setOptions] = useState<string[]>([]);
    const [multiSelectFilteredOptions, setMultiSelectFilteredOptions] = useState<string[]>([]);
    const [multiSelectSearchValue, setMultiSelectSearchValue] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
    const openPopover = Boolean(anchorEl);
    const popoverId = openPopover ? "filter-popover" : undefined;

    useEffect(() => {
      if (filterOpened) {
        if (appliedFilters.includes(currentColumnId)) {
          const filterCondition = filterConditions[currentColumnId];
          if (filterCondition) {
            setTempFilterText(filterCondition.text as FilterText);
            setTempFilterOption(
              (filterCondition.option as FilterOptionKey) ||
                (filterType ? DEFAULT_FILTER_OPTIONS[filterType].option : ""),
            );
          }
        } else if (filterType) {
          setTempFilterOption(DEFAULT_FILTER_OPTIONS[filterType].option);
          setTempFilterText(DEFAULT_FILTER_OPTIONS[filterType].text);
        }
      }
      setFilterOpened(false);
    }, [filterOpened]);

    useEffect(() => {
      if (currentColumnId) {
        if (!filterConditions[currentColumnId]) {
          setFilterConditions((prev) => ({
            ...prev,
            // Seeded before the user picks anything, so `option`/`text` are not set yet.
            [currentColumnId]: {
              ...prev[currentColumnId],
              id: currentColumnId,
              type: filterType,
              predicate: [
                {
                  field: currentColumnId,
                  operator: filterType ? DEFAULT_FILTER_OPTIONS[filterType].option : "equal",
                  value: filterType ? DEFAULT_FILTER_OPTIONS[filterType].text : "",
                },
              ],
            } as FilterCondition,
          }));
        }
      }
    }, [currentColumnId]);

    useEffect(() => {
      handlePreAppliedFilter();
    }, [preAppliedFilters]);

    const buildEmptyNotEmptyPredicate = (columnId: string, option: FilterOptionKey) => {
      let predicateCondition: "isempty" | "isnotempty" | undefined;
      if (option === "null" || option === "empty") {
        predicateCondition = "isempty";
      } else if (option === "notnull" || option === "notempty") {
        predicateCondition = "isnotempty";
      }
      return predicateCondition
        ? [
            {
              field: columnId,
              operator: predicateCondition,
            },
          ]
        : [];
    };

    const buildDateBetweenPredicates = (columnId: string, filterText: { startDate?: Dayjs; endDate?: Dayjs }) => {
      if (!filterText || (!filterText.startDate && !filterText.endDate)) {
        return [
          {
            field: columnId,
            operator: "greaterthanorequal",
            value: dayjs().subtract(1, "day").format(),
          },
          {
            field: columnId,
            operator: "lessthanorequal",
            value: dayjs().subtract(1, "day").format(),
          },
        ];
      }
      const predicates: FilterPredicate[] = [];
      if (filterText.startDate) {
        const startDateStr = dayjs(filterText.startDate).startOf("day").format();
        predicates.push({
          field: columnId,
          operator: "greaterthanorequal",
          value: startDateStr,
        });
      }
      if (filterText.endDate) {
        const endDateStr = dayjs(filterText.endDate).endOf("day").format();
        predicates.push({
          field: columnId,
          operator: "lessthanorequal",
          value: endDateStr,
        });
      }
      if (predicates.length) return predicates;
      return [
        {
          field: columnId,
          operator: "equal",
          value: dayjs().format(),
        },
      ];
    };

    const buildDateEqualPredicates = (columnId: string, filterText: { date?: Dayjs }) => {
      if (!filterText?.date) {
        const defaultStartDate = dayjs().startOf("day").format();
        const defaultEndDate = dayjs().endOf("day").format();
        return [
          {
            field: columnId,
            operator: "greaterthanorequal",
            value: defaultStartDate,
          },
          {
            field: columnId,
            operator: "lessthanorequal",
            value: defaultEndDate,
          },
        ];
      }
      const d = dayjs(filterText.date);
      const startDate = d.startOf("day").format();
      const endDate = d.endOf("day").format();
      return [
        {
          field: columnId,
          operator: "greaterthanorequal",
          value: startDate,
        },
        {
          field: columnId,
          operator: "lessthanorequal",
          value: endDate,
        },
      ];
    };

    const buildDateOtherPredicates = (columnId: string, option: FilterOptionKey, filterText: { date?: Dayjs }) => {
      if (!filterText?.date) {
        return [
          {
            field: columnId,
            operator: option,
            value: dayjs().format(),
          },
        ];
      }
      let formatted = dayjs().format();
      const hasValidDate = filterText.date && dayjs(filterText.date).isValid();
      if (hasValidDate) {
        formatted = dayjs.isDayjs(filterText.date) ? filterText.date.format() : dayjs(filterText.date).format();
      }
      return [
        {
          field: columnId,
          operator: option,
          value: formatted,
        },
      ];
    };

    const buildMultiSelectPredicates = (columnId: string, filterText: FilterText) => {
      const selectedValues = Array.isArray(filterText) ? filterText : [];
      if (selectedValues.length === 0) {
        return [];
      }
      return selectedValues.map((value: string) => ({
        field: columnId,
        operator: "equal",
        value,
      }));
    };

    const buildSimplePredicate = (
      columnId: string,
      option: FilterOptionKey,
      type: FilterType,
      filterText: FilterText,
    ) => {
      const defaultValue = type === "Number" ? 0 : "";
      let filterValue;
      if (filterText === undefined) {
        filterValue = defaultValue;
      } else if (type === "Number") {
        filterValue = Number(filterText);
      } else {
        filterValue = filterText;
      }
      return [
        {
          field: columnId,
          operator: option,
          value: filterValue,
        },
      ];
    };

    const handlePreAppliedFilter = () => {
      if (preAppliedFilters) {
        const predicateUpdatedFilter = Object.keys(preAppliedFilters).reduce<FilterConditionsMap>((acc, filterKey) => {
          // SHAPE GAP: `filterType` is read here but neither this component's `FilterCondition` nor
          // the grid's declares it — callers of `preAppliedFilters` pass it in anyway.
          const f = preAppliedFilters[filterKey]! as FilterCondition & { filterType?: FilterType };
          acc[filterKey] = {
            ...structuredClone(f),
            predicate: getTempPredicateArray(
              f.text as FilterText,
              f.option as FilterOptionKey,
              f.filterType!,
              filterKey,
            ),
          };
          return acc;
        }, {});
        setFilterConditions(structuredClone(predicateUpdatedFilter));
        getFilterPredicates(predicateUpdatedFilter, true);
        setAppliedFilters(Object.keys(preAppliedFilters));
      }
    };

    useEffect(() => {
      if (!filterType || !filterConditions[currentColumnId]) return;
      if ((tempFilterOption === "equal" || filterType === "MultiSelect") && filterType !== "Number") {
        fetchOptions("", currentColumnId, "startswith");
      }
      if (optionsOpen && (filterType === "Text" || filterType === "Number") && tempFilterOption !== "equal") {
        fetchOptions(tempFilterText, currentColumnId, tempFilterOption);
      }
    }, [optionsOpen, tempFilterText, tempFilterOption]);

    const handleCloseOptions = () => setOptionsOpen(false);
    const handleClosePopover = () => {
      setTempFilterText("");
      setTempFilterOption("");
      setMultiSelectSearchValue("");
      setMultiSelectFilteredOptions([]);
      if (!appliedFilters.includes(currentColumnId)) {
        setFilterConditions((prev) => {
          const newState = { ...prev };
          delete newState[currentColumnId];
          return newState;
        });
      }
      setAnchorEl(null);
      setOptionsOpen(false);
    };

    const getFilterPredicates = (updatedFilterConditions: FilterConditionsMap, callForData: boolean) => {
      const predicates: Array<FilterPredicate | FilterPredicateGroup> = [];
      let whereClause: FilterWhereClause = [];
      if (Object.keys(updatedFilterConditions).length === 0 && callForData) {
        setFilterPredicates("");
      }
      for (const filterkey of Object.keys(updatedFilterConditions)) {
        const filterCondition = updatedFilterConditions[filterkey]!;
        const enhancedPredicates = (filterCondition.predicate || []).map((predicate) => ({
          ...predicate,
          isComplex: false,
          ignoreCase: true,
          ignoreAccent: false,
        }));
        if (filterCondition.type === "MultiSelect") {
          predicates.push({
            isComplex: true,
            ignoreCase: true,
            ignoreAccent: false,
            condition: "or",
            predicates: enhancedPredicates,
          });
        } else {
          predicates.push(...enhancedPredicates);
        }
      }
      if (predicates.length > 0) {
        whereClause = {
          isComplex: true,
          ignoreCase: true,
          ignoreAccent: false,
          condition: "and",
          predicates,
        };
      }
      if (callForData) {
        setFilterPredicates(whereClause as Record<string, unknown>);
      }
      return whereClause;
    };

    const getTempPredicateArray = (
      filterText: FilterText,
      filterOption: FilterOptionKey | "",
      type: FilterType,
      columnId: string,
    ): FilterPredicate[] => {
      if (!columnId) return [];
      const option: FilterOptionKey =
        (filterOption as FilterOptionKey) || DEFAULT_FILTER_OPTIONS[type]?.option || "equal";
      if (filterOption && TEXT_FIELD_DISABLED_OPTIONS.has(option)) {
        return buildEmptyNotEmptyPredicate(columnId, option);
      }
      if (type === "Date") {
        if (option === "between") {
          return buildDateBetweenPredicates(columnId, filterText as { startDate?: Dayjs; endDate?: Dayjs });
        }
        if (option === "equal") {
          return buildDateEqualPredicates(columnId, filterText as { date?: Dayjs });
        }
        return buildDateOtherPredicates(columnId, option, filterText as { date?: Dayjs });
      }
      if (type === "MultiSelect") {
        return buildMultiSelectPredicates(columnId, filterText);
      }
      return buildSimplePredicate(columnId, option, type, filterText);
    };

    const handleSearchInMultiSelectOptions = (searchText: string) => {
      setMultiSelectSearchValue(searchText);
      if (searchText) {
        const filtered = options.filter(
          (option) => option !== "No Records Found" && option.toLowerCase().includes(searchText.toLowerCase()),
        );
        setMultiSelectFilteredOptions(filtered.length > 0 ? filtered : ["No Records Found"]);
        return;
      }
      setMultiSelectFilteredOptions(options);
    };

    const clearFilterForCurrentColumn = () => {
      const button = "mdi mdi-filter-variant text-slate-900";
      const updatedColumns = gridRef.current.slickGrid.getColumns().map((col) => {
        if (col.field === currentColumnId) {
          col.header.buttons[0]!.cssClass = button;
          col.filterApplied = false;
        }
        return col;
      });
      const updatedFilterConditions = { ...filterConditions };
      delete updatedFilterConditions[currentColumnId];
      setFilterConditions(updatedFilterConditions);
      if (Object.keys(updatedFilterConditions).length === 0) {
        setFilterPredicates("");
      }
      gridRef.current.slickGrid.setColumns(updatedColumns);
      setAppliedFilters((prev) => prev.filter((data) => data !== currentColumnId));
      if (Object.keys(updatedFilterConditions).length && remoteDataSource) {
        getFilterPredicates(updatedFilterConditions, true);
      }
    };

    const applyFilterForCurrentColumn = () => {
      if (!filterType) return;
      const predicateArray = getTempPredicateArray(tempFilterText, tempFilterOption, filterType, currentColumnId);
      if (currentColumnId) {
        const newFilterConditions = {
          ...filterConditions,
          [currentColumnId]: {
            ...filterConditions[currentColumnId],
            option: tempFilterOption,
            text: tempFilterText,
            predicate: predicateArray,
          } as FilterCondition,
        };
        setFilterConditions((prev) => ({ ...prev, ...newFilterConditions }));
        setAppliedFilters((prev) => (prev.includes(currentColumnId) ? [...prev] : [...prev, currentColumnId]));
        if (onFilterConditionsChange) {
          onFilterConditionsChange(Object.keys(newFilterConditions), newFilterConditions);
        }
      }
      const button = "mdi mdi-filter-variant text-[var(--primary-gradient-color1)]";
      const updatedColumns = gridRef.current.slickGrid.getColumns().map((col) => {
        if (col.field === currentColumnId) {
          col.header.buttons[0]!.cssClass = button;
          col.filterApplied = true;
        }
        return col;
      });
      gridRef.current.slickGrid.setColumns(updatedColumns);
      if (Object.keys(filterConditions).length && remoteDataSource) {
        getFilterPredicates(
          {
            ...filterConditions,
            [currentColumnId]: {
              ...filterConditions[currentColumnId],
              option: tempFilterOption,
              text: tempFilterText,
              predicate: predicateArray,
            } as FilterCondition,
          },
          true,
        );
      }
    };

    const handleApplyFilter = (clearFilter = false) => {
      if (!filterType) return;
      if (clearFilter) {
        clearFilterForCurrentColumn();
      } else {
        applyFilterForCurrentColumn();
      }
      setAnchorEl(null);
      setOptions([]);
      setOptionsOpen(false);
      setTempFilterText("");
      setTempFilterOption("");
      setCurrentColumnId("");
    };

    const getDateValue = (dateValue: Dayjs, dateType: string) => {
      let modifiedDate = dateValue;
      if (dateType === "lessthan") {
        modifiedDate = dateValue.startOf("day");
      } else if (dateType === "lessthanorequal") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthan") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthanorequal") {
        modifiedDate = dateValue.startOf("day");
      }
      return modifiedDate;
    };

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
      const v = String(event.target.value) as FilterOptionKey;
      setTempFilterOption(v);
      const filterValue: Dayjs = (tempFilterText as { date?: Dayjs })?.date ?? dayjs();
      if (filterType === "Date") {
        if (v === "between") {
          setTempFilterText({ startDate: dayjs(), endDate: dayjs() });
        } else if (v === "equal") {
          setTempFilterText({ date: dayjs() });
        } else {
          setTempFilterText({ date: getDateValue(filterValue, v) });
        }
      }
    };

    const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
      setOptionsOpen(Boolean(newInputValue) && newInputValue !== tempFilterText);
      setTempFilterText(newInputValue);
    };

    const handleDateChange = (newDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (newDate) {
        const dayjsObject = dayjs(newDate);
        setTempFilterText({ date: getDateValue(dayjsObject, String(tempFilterOption)) });
      }
    };

    const handleBetweenStartDateChange = (startDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (startDate) {
        const dayjsObject = dayjs(startDate);
        setTempFilterText((prev) => ({ ...(prev as { startDate?: Dayjs; endDate?: Dayjs }), startDate: dayjsObject }));
      }
    };

    const handleBetweenEndDateChange = (endDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (endDate) {
        const dayjsObject = dayjs(endDate);
        setTempFilterText((prev) => ({ ...(prev as { startDate?: Dayjs; endDate?: Dayjs }), endDate: dayjsObject }));
      }
    };

    interface RemoteDataResponse {
      result?: FilterRow[];
      count?: number;
    }

    const fetchRemoteData = async (
      remoteDataSource: string,
      query: Props["query"],
      beforeDataBound?: Props["beforeDataBound"],
    ) => {
      const modifiedQuery = { ...query, skip: 0, take: 1000, requiresCounts: true };
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(remoteDataSource, { value: modifiedQuery });
      const bd = beforeDataBound?.(response);
      return (bd ?? (Array.isArray(response?.data?.result) ? response.data.result : response?.data?.result)) as
        | FilterRow[]
        | undefined;
    };

    const fetchRemoteFilteredData = async (
      remoteDataSource: string,
      query: Props["query"],
      filterConditions: FilterConditionsMap,
      currentColumnId: string,
      filterOption: string,
      tempFilterText: FilterText,
      beforeDataBound?: Props["beforeDataBound"],
    ) => {
      // Only `text`/`predicate` matter to `getUpdatedFilterConditions` on this path; the other members
      // of the stored condition are not read while building the option list.
      const updatedConditions = {
        ...filterConditions,
        [currentColumnId]: {
          text: tempFilterText,
          predicate: [{ field: currentColumnId, operator: filterOption, value: tempFilterText }],
        },
      } as FilterConditionsMap;
      const updatedFilterConditions = getUpdatedFilterConditions(updatedConditions);
      const predicate = getFilterPredicates(updatedFilterConditions, false);
      const modifiedQuery = { ...query, skip: 0, take: 20, requiresCounts: true };
      modifiedQuery.where = query?.where?.length ? [...query.where, predicate] : [predicate];
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(remoteDataSource, { value: modifiedQuery });
      const bd = beforeDataBound?.(response);
      return (bd ?? (Array.isArray(response?.data?.result) ? response.data.result : response?.data?.result)) as
        | FilterRow[]
        | undefined;
    };

    const extractMultiSelectValues = (
      filteredData: FilterRow[],
      field: string,
      valueGetter?: ColumnDefinition["_valueGetter"],
    ) => {
      const allValues: string[] = [];
      for (const data of filteredData ?? []) {
        const fieldValue = valueGetter ? valueGetter(data) : get(data, field);
        if (Array.isArray(fieldValue)) {
          for (const item of fieldValue) {
            allValues.push((item as { name?: string })?.name || item);
          }
        } else if (fieldValue) {
          allValues.push((fieldValue as { name?: string })?.name || (fieldValue as string));
        }
      }
      const unique = [...new Set(allValues.filter(Boolean))] as string[];
      return unique.length ? unique.sort((a, b) => a.localeCompare(b)) : ["No Records Found"];
    };

    const extractSingleSelectValues = (
      filteredData: FilterRow[],
      field: string,
      valueGetter?: ColumnDefinition["_valueGetter"],
    ) => {
      const values = [
        ...new Set(
          (filteredData ?? []).map((data) => {
            const val = valueGetter ? valueGetter(data) : get(data, field);
            if (val == null) return "";
            return String(val);
          }),
        ),
      ] as string[];
      return values.length ? values : ["No Records Found"];
    };

    const fetchOptions = async (text: FilterText, field: string, filterOption: string) => {
      if (!filterType) return;
      const shouldFetch = filterType === "MultiSelect" || tempFilterOption === "equal" || Boolean(text);
      if (!shouldFetch) return;
      setLoading(true);
      let filteredData: FilterRow[] | undefined;
      if (filterType === "MultiSelect") {
        if (remoteDataSource && query) {
          filteredData = await fetchRemoteData(remoteDataSource, query, beforeDataBound);
        } else if (localData) {
          filteredData = datasetForFetchingOptions;
        }
      } else if (remoteDataSource && query) {
        filteredData = await fetchRemoteFilteredData(
          remoteDataSource,
          query,
          filterConditions,
          currentColumnId,
          filterOption,
          tempFilterText,
          beforeDataBound,
        );
      } else if (localData) {
        filteredData = applyFilter(datasetForFetchingOptions, currentColumnId, filterOption, text, filterType);
      }
      const valueGetter = columnDefinitions.find((col) => col.field === currentColumnId)?._valueGetter;
      if (filterType === "MultiSelect") {
        const optionsList = extractMultiSelectValues(filteredData ?? [], field, valueGetter);
        setOptions(optionsList);
        setMultiSelectFilteredOptions(optionsList);
      } else {
        const optionsList = extractSingleSelectValues(filteredData ?? [], field, valueGetter);
        setOptions(optionsList);
      }
      setLoading(false);
    };

    const getFilterOptions = () => {
      if (filterType) {
        return FILTER_MAPPER[filterType].map((selectedOption: string) => (
          <MenuItem key={selectedOption} value={`${selectedOption.toLowerCase().replaceAll(/\s+/g, "")}`}>
            {selectedOption}
          </MenuItem>
        ));
      }
      return null;
    };

    const isTextFieldDisabled = tempFilterOption ? TEXT_FIELD_DISABLED_OPTIONS.has(tempFilterOption) : false;
    const datePickerValueLeft =
      tempFilterOption === "between"
        ? (tempFilterText as { startDate?: Dayjs })?.startDate || dayjs()
        : (tempFilterText as { date?: Dayjs })?.date || dayjs();
    const datePickerValueRight = (tempFilterText as { endDate?: Dayjs })?.endDate || dayjs();
    const popoverWidth = filterType === "Date" && tempFilterOption === "between" ? "22.5rem" : "15.625rem";

    return (
      <div>
        {filterType && (
          <Popover
            id={popoverId}
            open={Boolean(anchorEl)}
            anchorReference="anchorPosition"
            anchorPosition={anchorEl || { top: 0, left: 0 }}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
                borderRadius: "0.5rem",
                padding: "1.25rem",
                width: `${popoverWidth}`,
                marginTop: "0.625rem",
                marginInlineStart: "0.625rem",
              },
            }}
          >
            <div>
              <FormControl fullWidth>
                <Select
                  value={tempFilterOption}
                  displayEmpty
                  onChange={handleFilterChange}
                  style={{ marginBottom: "1rem" }}
                >
                  {filterType && getFilterOptions()}
                </Select>
              </FormControl>
              {(filterType === "Text" || filterType === "Number") && (
                <Autocomplete
                  freeSolo
                  open={optionsOpen}
                  options={options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={typeof tempFilterText === "string" ? tempFilterText : String(tempFilterText ?? "")}
                  filterOptions={(x) => x}
                  onInputChange={handleInputChange}
                  disabled={isTextFieldDisabled}
                  renderOption={(props, option) => (
                    <li {...props} style={{ minWidth: "12.5rem" }}>
                      {renderOption && renderOption(props, option, currentColumnId)
                        ? renderOption(props, option, currentColumnId)
                        : option || (
                            <em>
                              <Text>{t("empty")}</Text>
                            </em>
                          )}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={`${isTextFieldDisabled ? "filled-disabled" : "outlined-helperText"}`}
                      disabled={isTextFieldDisabled}
                      placeholder={t("Enter the Value")}
                      type={filterType === "Number" ? "number" : "text"}
                      onClick={() => {
                        if (tempFilterOption === "equal") {
                          setOptionsOpen(true);
                        }
                      }}
                    />
                  )}
                />
              )}
              {filterType === "MultiSelect" && (
                <Autocomplete
                  multiple
                  open={optionsOpen}
                  options={multiSelectFilteredOptions.length > 0 ? multiSelectFilteredOptions : options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={Array.isArray(tempFilterText) ? (tempFilterText as string[]) : []}
                  filterOptions={(x) => x}
                  inputValue={multiSelectSearchValue}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      handleSearchInMultiSelectOptions(newInputValue);
                      setOptionsOpen(true);
                    }
                  }}
                  onChange={(_, newValue) => {
                    setTempFilterText(newValue.filter((val) => val !== "No Records Found"));
                    setMultiSelectSearchValue("");
                    setMultiSelectFilteredOptions(options);
                  }}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ minWidth: "12.5rem" }}>
                      <Checkbox
                        style={{ marginInlineEnd: "0.5rem" }}
                        checked={selected}
                        disabled={option === "No Records Found"}
                      />
                      {option || (
                        <em>
                          <Text>{t("empty")}</Text>
                        </em>
                      )}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("Enter the value")}
                      onClick={() => {
                        setOptionsOpen(true);
                        if (multiSelectFilteredOptions.length === 0 && options.length > 0) {
                          setMultiSelectFilteredOptions(options);
                        }
                      }}
                    />
                  )}
                />
              )}
              {filterType === "Date" && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <DatePicker
                      disabled={tempFilterOption ? TEXT_FIELD_DISABLED_OPTIONS.has(tempFilterOption) : false}
                      onChange={tempFilterOption === "between" ? handleBetweenStartDateChange : handleDateChange}
                      value={datePickerValueLeft}
                    />
                    {tempFilterOption === "between" && (
                      <DatePicker onChange={handleBetweenEndDateChange} value={datePickerValueRight} />
                    )}
                  </div>
                </LocalizationProvider>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.625rem",
                  paddingTop: "1rem",
                }}
              >
                <ButtonComponent
                  buttonType="gradientButton"
                  style={{ flex: "1", height: "2.1875rem", borderRadius: "0.5rem" }}
                  onClick={() => handleApplyFilter(false)}
                  data-testid="applyFilter"
                >
                  <Text>{t("Apply")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType="borderButton"
                  style={{ flex: "1", height: "2.1875rem", borderRadius: "0.5rem" }}
                  onClick={() => handleApplyFilter(true)}
                  data-testid="clearFilter"
                >
                  <Text>{t("Clear")}</Text>
                </ButtonComponent>
              </div>
            </div>
          </Popover>
        )}
      </div>
    );
  },
);

FilterComponent.displayName = "FilterComponent";
export default FilterComponent;
